import { useSyncExternalStore } from "react";
import { deriveHistoryState, makeUrl, navigate, routes } from "../lib/urls";

// The foreground modal lives in the URL as `?modal=<type>` plus its params
// under `modal[*]`; the stack beneath it is transient, kept in history.state.
// Nothing opens a modal imperatively — navigating to a case is opening it, and
// the stack maintains itself from navigation via deriveHistoryState.
const MODAL = "modal";

export type ModalType = keyof typeof routes.modal.cases;
type Instance = { type: ModalType; values: Record<string, string> };
export type ModalEntry = Instance & { id: string; key: string };

const idOf = (e: Instance) => e.values.id ?? e.values.spaceId ?? "";
const same = (a: Instance, b: Instance) =>
  a.type === b.type && idOf(a) === idOf(b);

function parseForeground(search: string): Instance | null {
  const params = new URLSearchParams(search);
  const type = params.get(MODAL) as ModalType | null;
  if (!type) return null;
  const values: Record<string, string> = {};
  for (const [k, v] of params) {
    if (k.startsWith(`${MODAL}[`)) values[k.slice(MODAL.length + 1, -1)] = v;
  }
  return { type, values };
}

// The URL foreground is the top modal; history.state holds the rest. A mismatch
// (cold load, shared link, page nav) collapses to just the foreground.
function reconcile(): Instance[] {
  const fg = parseForeground(
    typeof location === "undefined" ? "" : location.search
  );
  if (!fg) return [];
  const beneath = (typeof history === "undefined" ? null : history.state)
    ?.modalStack as Instance[] | undefined;
  return beneath?.length && same(beneath.at(-1)!, fg)
    ? [...beneath.slice(0, -1), fg]
    : [fg];
}

// The stack is the modal leg of browser history, so like history it is never
// deduplicated: opening a modal that is already open beneath pushes another
// instance rather than raising the existing one. Only navigating within the
// top modal (same type and id, e.g. switching tabs) updates it in place.
deriveHistoryState((url, state) => {
  const prev = reconcile();
  const fg = parseForeground(
    url.includes("?") ? url.slice(url.indexOf("?")) : ""
  );
  const next = !fg
    ? []
    : prev.length && same(prev.at(-1)!, fg)
      ? [...prev.slice(0, -1), fg]
      : [...prev, fg];
  return { ...(state as Record<string, unknown>), modalStack: next };
});

let cached: ModalEntry[] = [];
let cachedKey = "";
// The same modal can appear more than once in the stack, so each entry's key
// carries an occurrence index — stable because the stack only changes at the
// top.
function snapshot(): ModalEntry[] {
  const seen = new Map<string, number>();
  const entries = reconcile().map((e) => {
    const id = `${e.type}-${idOf(e)}`;
    const nth = seen.get(id) ?? 0;
    seen.set(id, nth + 1);
    return { ...e, id: idOf(e), key: `${id}-${nth}` };
  });
  const k = JSON.stringify(entries);
  if (k !== cachedKey) {
    cachedKey = k;
    cached = entries;
  }
  return cached;
}

const subscribe = (listener: () => void) => {
  addEventListener("popstate", listener);
  return () => removeEventListener("popstate", listener);
};

// The `modal*` params reflecting a foreground modal, clearing whatever is in
// the URL now — page params untouched.
function modalParams(fg?: Instance): Record<string, string> {
  const params: Record<string, string> = {};
  const search = typeof location === "undefined" ? "" : location.search;
  for (const k of new URLSearchParams(search).keys()) {
    if (k === MODAL || k.startsWith(`${MODAL}[`)) params[k] = "";
  }
  if (fg) {
    params[MODAL] = fg.type;
    for (const [p, v] of Object.entries(fg.values)) {
      params[`${MODAL}[${p}]`] = v;
    }
  }
  return params;
}

// Closing reveals the modal beneath. Which modal disappears cannot be derived
// from the URL change alone (revealing X and raising X land on the same URL),
// so this is the one operation that composes history.state explicitly.
export function closeModal() {
  const next = reconcile().slice(0, -1);
  history.replaceState(
    { ...history.state, modalStack: next },
    "",
    makeUrl(null, modalParams(next.at(-1)))
  );
}

// Spread into a makeUrl() call to leave the modal stack for a full page.
export const closeModalStack = () => modalParams();

export function openRoleModal(roleId: string) {
  if (roleId === "tenant_admin") navigate(routes.modal.admins);
  else navigate(routes.modal.role, { id: roleId });
}

export function useModalStack(): ModalEntry[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function useModalId(type: ModalType) {
  return useModalStack().findLast((e) => e.type === type)?.id;
}
